import { createContext, useContext } from 'react';

/**
 * True for a page rendered in the settings pane, beside the settings nav.
 * Every pane starts at the pane's top left, so a page that centers or pads
 * itself as a standalone page drops that in the pane, and a page that heads
 * itself uses `SETTINGS_PANE_TITLE`.
 */
export const SettingsPaneContext = createContext(false);

export function useInSettingsPane(): boolean {
  return useContext(SettingsPaneContext);
}

/** The title a settings page heads itself with in the pane. */
export const SETTINGS_PANE_TITLE = 'text-lg font-semibold text-foreground';

/**
 * A pane's page: its title row, then its sections, filling the pane up to
 * the one width every pane shares.
 */
export const SETTINGS_PANE = 'flex w-full max-w-[1600px] flex-col gap-6';

/** A pane's title row: the title, and the pane's actions at the right. */
export const SETTINGS_PANE_HEADER =
  'flex min-h-8 flex-wrap items-center justify-between gap-3';

/**
 * A section of a pane: a hairline above it, and no frame around it. Sections
 * sit flat on the pane, never as cards inside it.
 */
export const SETTINGS_PANE_SECTION =
  'border-t border-black/[0.06] pt-5 dark:border-white/[0.06]';

/** A section's title row: the title, and the section's actions. */
export const SETTINGS_PANE_SECTION_HEADER =
  'flex min-h-8 flex-wrap items-center justify-between gap-3';

/** A section's title. */
export const SETTINGS_PANE_SECTION_TITLE =
  'text-sm font-medium text-foreground';

/** A hairline between one part of a section and the next. */
export const SETTINGS_PANE_HAIRLINE =
  'border-black/[0.06] dark:border-white/[0.06]';

/** The hairlines between a section's rows. */
export const SETTINGS_PANE_ROWS =
  'divide-y divide-black/[0.06] dark:divide-white/[0.06]';
